import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useInvoices } from "@/context/InvoiceContext";
import { useStock } from "@/context/StockContext";
import { Invoice, InvoiceFormData } from "@/types/stock";
import { Plus, Trash2, Calculator, User, FileText } from "lucide-react";
import { CreditLimitWarning } from "@/components/clients/CreditLimitWarning";

interface InvoiceFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice?: Invoice | null;
  onSubmit: (data: InvoiceFormData) => Promise<void>;
}

const InvoiceFormDialog: React.FC<InvoiceFormDialogProps> = ({
  isOpen,
  onClose,
  invoice,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { clients } = useInvoices();
  const { products } = useStock();
  const [loading, setLoading] = useState(false);
  const [creditValidation, setCreditValidation] = useState<{
    canCreate: boolean;
    reason?: string;
  }>({ canCreate: true });
  const [formData, setFormData] = useState<InvoiceFormData>({
    clientId: "",
    clientName: "",
    clientPhone: "",
    clientEmail: "",
    clientAddress: "",
    taxRate: 19, // Default VAT rate in Tunisia
    discountAmount: 0,
    dueDate: undefined,
    notes: "",
    termsAndConditions: "",
    items: [],
  });

  useEffect(() => {
    if (invoice) {
      setFormData({
        clientId: invoice.clientId || "",
        clientName: invoice.clientName,
        clientPhone: invoice.clientPhone || "",
        clientEmail: invoice.clientEmail || "",
        clientAddress: invoice.clientAddress || "",
        taxRate: invoice.taxRate,
        discountAmount: invoice.discountAmount,
        dueDate: invoice.dueDate,
        notes: invoice.notes || "",
        termsAndConditions: invoice.termsAndConditions || "",
        items: invoice.items.map((item) => ({
          productId: item.productId,
          productName: item.productName,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discountPercentage: item.discountPercentage,
        })),
      });
    } else {
      setFormData({
        clientId: "",
        clientName: "",
        clientPhone: "",
        clientEmail: "",
        clientAddress: "",
        taxRate: 19,
        discountAmount: 0,
        dueDate: undefined,
        notes: "",
        termsAndConditions: "",
        items: [],
      });
    }
  }, [invoice, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientName.trim() || formData.items.length === 0) return;

    setLoading(true);
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof InvoiceFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClientSelect = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      setFormData((prev) => ({
        ...prev,
        clientId: client.id,
        clientName: client.name,
        clientPhone: client.phone || "",
        clientEmail: client.email || "",
        clientAddress: client.address || "",
      }));
    } else {
      // Manual client entry
      setFormData((prev) => ({
        ...prev,
        clientId: "",
        clientName: "",
        clientPhone: "",
        clientEmail: "",
        clientAddress: "",
      }));
    }
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: "",
          productName: "",
          description: "",
          quantity: 1,
          unitPrice: 0,
          discountPercentage: 0,
        },
      ],
    }));
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateItem = (index: number, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleProductSelect = (index: number, productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      updateItem(index, "productId", product.id);
      updateItem(index, "productName", product.name);
      updateItem(index, "unitPrice", product.price);
    }
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => {
    const itemTotal = item.quantity * item.unitPrice;
    const discount = (itemTotal * item.discountPercentage) / 100;
    return sum + (itemTotal - discount);
  }, 0);

  const taxAmount = (subtotal * formData.taxRate) / 100;
  const totalAmount = subtotal + taxAmount - formData.discountAmount;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {invoice ? t("invoices.edit") : t("invoices.create")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-4 w-4" />
                {t("invoices.clientInformation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client">{t("invoices.client")}</Label>
                  <Select
                    value={formData.clientId || "manual"}
                    onValueChange={handleClientSelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("invoices.selectClient")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">
                        {t("invoices.manualEntry")}
                      </SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          <div className="flex items-center gap-2">
                            <span>{client.name}</span>
                            {client.phone && (
                              <Badge variant="outline" className="text-xs">
                                {client.phone}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientName">
                    {t("invoices.clientName")} *
                  </Label>
                  <Input
                    id="clientName"
                    value={formData.clientName}
                    onChange={(e) =>
                      handleInputChange("clientName", e.target.value)
                    }
                    placeholder={t("invoices.clientNamePlaceholder")}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientPhone">
                    {t("invoices.clientPhone")}
                  </Label>
                  <Input
                    id="clientPhone"
                    value={formData.clientPhone}
                    onChange={(e) =>
                      handleInputChange("clientPhone", e.target.value)
                    }
                    placeholder={t("invoices.clientPhonePlaceholder")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientEmail">
                    {t("invoices.clientEmail")}
                  </Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) =>
                      handleInputChange("clientEmail", e.target.value)
                    }
                    placeholder={t("invoices.clientEmailPlaceholder")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientAddress">
                  {t("invoices.clientAddress")}
                </Label>
                <Textarea
                  id="clientAddress"
                  value={formData.clientAddress}
                  onChange={(e) =>
                    handleInputChange("clientAddress", e.target.value)
                  }
                  placeholder={t("invoices.clientAddressPlaceholder")}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Credit Limit Warning */}
          {formData.clientId &&
            formData.clientId !== "manual" &&
            formData.clientId.trim() !== "" &&
            totalAmount > 0 && (
              <CreditLimitWarning
                clientId={formData.clientId}
                invoiceAmount={totalAmount}
                onCreditCheck={(canCreate, reason) => {
                  setCreditValidation({ canCreate, reason });
                }}
              />
            )}

          {/* Invoice Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calculator className="h-4 w-4" />
                  {t("invoices.items")}
                </div>
                <Button type="button" onClick={addItem} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  {t("invoices.addItem")}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {formData.items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {t("invoices.noItems")}
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.items.map((item, index) => (
                    <Card key={index} className="border-dashed">
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                          <div className="md:col-span-2">
                            <Label>{t("invoices.product")}</Label>
                            <Select
                              value={item.productId || "manual"}
                              onValueChange={(value) =>
                                value === "manual"
                                  ? updateItem(index, "productId", "")
                                  : handleProductSelect(index, value)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t("invoices.selectProduct")}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="manual">
                                  {t("invoices.manualEntry")}
                                </SelectItem>
                                {products.map((product) => (
                                  <SelectItem
                                    key={product.id}
                                    value={product.id}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span>{product.name}</span>
                                      <Badge
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        {product.price.toFixed(3)} TND
                                      </Badge>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              value={item.productName}
                              onChange={(e) =>
                                updateItem(index, "productName", e.target.value)
                              }
                              placeholder={t("invoices.productName")}
                              className="mt-2"
                            />
                          </div>

                          <div>
                            <Label>{t("invoices.quantity")}</Label>
                            <Input
                              type="number"
                              min="1"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) =>
                                updateItem(
                                  index,
                                  "quantity",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                            />
                          </div>

                          <div>
                            <Label>{t("invoices.unitPrice")}</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.001"
                              value={item.unitPrice}
                              onChange={(e) =>
                                updateItem(
                                  index,
                                  "unitPrice",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                            />
                          </div>

                          <div>
                            <Label>{t("invoices.discount")} (%)</Label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={item.discountPercentage}
                              onChange={(e) =>
                                updateItem(
                                  index,
                                  "discountPercentage",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                            />
                          </div>

                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium">
                              {(
                                item.quantity * item.unitPrice -
                                (item.quantity *
                                  item.unitPrice *
                                  item.discountPercentage) /
                                  100
                              ).toFixed(3)}{" "}
                              TND
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeItem(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="mt-2">
                          <Label>{t("invoices.description")}</Label>
                          <Input
                            value={item.description || ""}
                            onChange={(e) =>
                              updateItem(index, "description", e.target.value)
                            }
                            placeholder={t("invoices.itemDescription")}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card>
            <CardHeader>
              <CardTitle>{t("invoices.invoiceDetails")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="taxRate">{t("invoices.taxRate")} (%)</Label>
                  <Input
                    id="taxRate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.taxRate}
                    onChange={(e) =>
                      handleInputChange(
                        "taxRate",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="discountAmount">
                    {t("invoices.discountAmount")} (TND)
                  </Label>
                  <Input
                    id="discountAmount"
                    type="number"
                    min="0"
                    step="0.001"
                    value={formData.discountAmount}
                    onChange={(e) =>
                      handleInputChange(
                        "discountAmount",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dueDate">{t("invoices.dueDate")}</Label>
                  <Input
                    id="dueDate"
                    type="date"
                    value={
                      formData.dueDate
                        ? formData.dueDate.toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      handleInputChange(
                        "dueDate",
                        e.target.value ? new Date(e.target.value) : undefined
                      )
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">{t("invoices.notes")}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  placeholder={t("invoices.notesPlaceholder")}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="terms">
                  {t("invoices.termsAndConditions")}
                </Label>
                <Textarea
                  id="terms"
                  value={formData.termsAndConditions}
                  onChange={(e) =>
                    handleInputChange("termsAndConditions", e.target.value)
                  }
                  placeholder={t("invoices.termsPlaceholder")}
                  rows={3}
                />
              </div>

              {/* Totals Summary */}
              <div className="border-t pt-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t("invoices.subtotal")}:</span>
                    <span>{subtotal.toFixed(3)} TND</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      {t("invoices.tax")} ({formData.taxRate}%):
                    </span>
                    <span>{taxAmount.toFixed(3)} TND</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("invoices.discount")}:</span>
                    <span>-{formData.discountAmount.toFixed(3)} TND</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>{t("invoices.total")}:</span>
                    <span>{totalAmount.toFixed(3)} TND</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                loading ||
                !formData.clientName.trim() ||
                formData.items.length === 0 ||
                !creditValidation.canCreate
              }
            >
              {loading ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceFormDialog;
